<template>
	<Layout
		:loading="loading || saving"
		:pageTitle="title"
		:backlink="action == 'create' ? `/events` : `/events/${$route.params.id}`"
		:hideSidebar="true"
	>
		<Loader :show="loading" />

		<div class="main-container container" v-if="!loading">
			<div class="row row-xs mt-4 mb-4 pb-4">
				<FormulateForm ref="form" v-model="form" @submit="save">
					<div class="col-12 col-md-10 m-auto">
						<div class="card mb-3 p-4">
							<div class="fieldgroup">
								<div class="lbl required">
									<div class="label">Nombre del evento</div>
								</div>
								<div class="data">
									<FormulateInput
										name="name"
										validation-name="Nombre del evento"
										validation="required"
									/>
								</div>
							</div>

							<hr />

							<div class="fieldgroup">
								<div class="lbl required">
									<div class="label">Cuándo</div>
								</div>
								<div class="data">
									<div class="row row-xs">
										<div class="col-12 col-md-6">
											<FormulateInput
												type="date"
												label="Feha y hora de inicio"
												name="startAt"
												:displayFormat="'DD-MM-YYYY HH:mm'"
												validation-name="Fecha del evento"
												:validation="`required|after:${minStartAt}`"
												class="flex-grow-1"
												:min="minStartAt"
												@input="startAtSelected"
											/>
										</div>
										<div class="col-12 col-md-6">
											<FormulateInput
												type="date"
												label="Feha y hora de finalización"
												name="endAt"
												:displayFormat="'DD-MM-YYYY HH:mm'"
												validation-name="Fecha del evento"
												:validation="`required|after:${form.startAt}`"
												:min="form.startAt"
												:disabled="!form.startAt"
												class="flex-grow-1"
											/>
										</div>
									</div>
								</div>
							</div>

							<hr />

							<div class="fieldgroup">
								<div class="lbl required">
									<div class="label">Dónde</div>
								</div>
								<div class="data">
									<EventLocationForm :value="form.location" @updated="locationSelected" />
									<FormulateInput type="validation" name="location" validation="required" />
								</div>
							</div>

							<hr />

							<div class="fieldgroup">
								<div class="lbl">
									<div class="label">Información del evento</div>
								</div>
								<div class="data">
									<FormulateInput type="wysiwyg" name="description" height="200" />
								</div>
							</div>

							<hr />

							<div class="fieldgroup">
								<div class="lbl required">
									<div class="label">Imágen del evento</div>
								</div>
								<div class="data">
									<FormulateInput
										label="Imagen del evento"
										name="image"
										type="file"
										accept="images"
										listStyle=""
										:displayBlock="true"
										validation="required"
										help="Usa una imágen de buena calidad en formato 1:1. Tamaño recomendado 1080px x 1080px"
									/>
								</div>
							</div>

							<hr />

							<div class="fieldgroup">
								<div class="lbl">
									<div class="label">Restricciones de edad</div>
								</div>
								<div class="data">
									<FormulateInput
										name="minAge"
										type="number"
										:validation="`optional|min:0`"
										class="flex-grow-1"
										:help="
											`Indica la edad mínima para poder asistir al evento. Si no se indica, se considera que no hay restricción`
										"
									/>
								</div>
							</div>

							<hr />

							<div class="fieldgroup">
								<div class="lbl required">
									<div class="label">Categoría</div>
								</div>
								<div class="data">
									<FormulateInput
										name="category"
										type="select"
										validation="required"
										validation-name="Categoría"
										class="flex-grow-1"
										:options="categories"
									/>
								</div>
							</div>
							<hr />
							<div class="d-flex justify-content-between mt-4">
								<div class="btn btn-light" @click="$router.go(-1)">
									Cancelar
								</div>
								<div class="btn btn-primary disabled" v-if="saving">
									Guardando <span class="loader" />
								</div>
								<button type="submit" class="btn btn-primary" v-if="!saving">
									Continuar
								</button>
							</div>
						</div>
					</div>
				</FormulateForm>
			</div>
		</div>
	</Layout>
</template>

<script>
import gql from "graphql-tag"
import mixins from "@/mixins/mixins"
import formMixins from "@/mixins/formMixins"

import EventLocationForm from "./forms/EventLocationForm"

export default {
	name: "EventForm",

	components: {
		EventLocationForm
	},

	mixins: [mixins, formMixins],

	data() {
		return {
			title: "Crear evento",
			action: "create",
			eventId: null,
			categories: [],
			form: {
				name: null,
				startAt: null,
				endAt: null,
				description: null,
				image: null,
				location: null,
				minAge: null,
				category: null
			},

			minStartAt: null,

			loading: true,
			saving: false
		}
	},

	async beforeMount() {
		this.loading = true
		await this.fetchData()
		this.minStartAt = this.$moment().format("YYYY-MM-DD HH:mm")

		if (this.$route.params.id) {
			this.action = "update"
			this.title = `Editar evento`
			await this.load(this.$route.params.id)
		}
		this.loading = false
	},

	methods: {
		startAtSelected(data) {
			if ((data && !this.form.endAt) || this.$moment(data) > this.$moment(this.form.endAt)) {
				this.$nextTick(() => {
					this.form.endAt = this.$moment(data)
						.add(4, "hour")
						.format("YYYY-MM-DD HH:mm:00")
				})
			}
		},
		locationSelected(location) {
			this.form.location = location
		},

		async save() {
			if (!this.saving) {
				this.saving = true

				let data = {
					name: this.form.name,
					startAt: this.form.startAt,
					endAt: this.form.endAt,
					location: this.form.location.id,
					description: this.form.description,
					image: this.form.image ? this.form.image.id : null,
					category: this.form.category,
					minAge: parseInt(this.form.minAge)
				}

				if (this.action == "create") {
					const created = await this.create(data)
					if (created) this.$router.push(`/events/${created.id}/ticketGroups`)
				}
				if (this.action == "update") {
					await this.update(this.$route.params.id, data)
					this.$router.go(-1)
				}

				this.saving = false
			}
		},

		create(data) {
			return this.$apollo
				.mutate({
					mutation: gql`
						mutation($data: EventInput!) {
							eventCreate(data: $data) {
								id
							}
						}
					`,
					variables: {
						data
					}
				})
				.then(({ data }) => {
					this.loading = false
					this.saving = false
					return data.eventCreate
				})
				.catch((error) => {
					this.parseGQLErrors(error)
					this.loading = false
					return null
				})
		},

		update(id, data) {
			return this.$apollo
				.mutate({
					mutation: gql`
						mutation($id: ID!, $data: EventInput!) {
							eventUpdate(id: $id, data: $data) {
								id
							}
						}
					`,
					variables: {
						id,
						data
					}
				})
				.then(({ data }) => {
					this.loading = false
					this.saving = false
					return data.eventUpdate
				})
				.catch((error) => {
					this.parseGQLErrors(error)
					this.loading = false
					return null
				})
		},

		load(id) {
			return this.$apollo
				.query({
					query: gql`
						query($id: ID!) {
							event(id: $id) {
								id
								name
								startAt
								endAt
								description
								minAge
								category {
									id
								}
								image {
									id
									type
									filename
									url
									urlThumb
								}
								location {
									id
									name
									address
								}
								status
								statusText
								statusClass
							}
						}
					`,
					variables: { id },
					fetchPolicy: "no-cache"
				})
				.then(({ data }) => {
					this.form = {
						name: data.event.name,
						startAt: data.event.startAt,
						endAt: data.event.endAt,
						description: data.event.description,
						image: data.event.image,
						location: data.event.location,
						category: data.event.category?.id || null,
						minAge: data.event.minAge
					}
				})
				.catch(() => {
					this.$toast.error("El item que desea editar no existe")
					this.$router.go(-1)
				})
		},

		fetchData() {
			return this.$apollo
				.query({
					query: gql`
						query {
							eventCategories {
								data {
									id
									name
								}
							}
						}
					`,
					fetchPolicy: "network-only"
				})
				.then(({ data }) => {
					this.categories = [
						{
							value: null,
							label: "Seleccione una categoría"
						}
					]
					for (const item of data.eventCategories.data) {
						this.categories.push({ value: item.id, label: item.name })
					}
				})
		}
	}
}
</script>
<style scoped lang="scss">
.container /deep/ .formulate-form {
	max-width: 100%;
	width: 100%;
}
</style>
